// src/components/portal/TipDistributionView.js
//
// Owner-only portal tab for configuring and settling tip distribution.
//
// Modes: Keep Your Own | Tip Share (tip-out) | Tip Pool
//
// Tip Share options:
//   - Calculation base: % of tips collected OR % of net sales
//   - Food/drink split: different % for food vs bar sales (net_sales mode only)
//   - Comps: include or exclude comped amounts from the sales base
//
// Tip Pool options:
//   - Role-weighted hours (server=1.0, busser=0.75, host=0.5, etc.)
//   - Minimum hours to qualify for pool share
//   - Exclude owners & managers
//
// Global options:
//   - Settlement method: Cash (manual) or Payroll (auto-post to ledger)
//   - Settlement period: Shift/Day or Pay Week
//   - Auto-gratuity: treated as service charge (separate) or included in tip pool
//   - Minimum settlement amount: skip ledger entries below threshold

import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const _fns = getFunctions();
const _getConfig  = httpsCallable(_fns, 'getTipDistributionConfig');
const _saveConfig = httpsCallable(_fns, 'saveTipDistributionConfig');
const _settle     = httpsCallable(_fns, 'settleTipDistribution');
const _getHistory = httpsCallable(_fns, 'getTipDistributionHistory');

// ── Helpers ───────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 10);
const todayStr = () => new Date().toISOString().slice(0, 10);
const startOfDay = (s) => new Date(s + 'T00:00:00').getTime();
const endOfDay   = (s) => new Date(s + 'T23:59:59.999').getTime();
const fmtDate    = (ms) => ms ? new Date(ms).toLocaleDateString() : '—';
const fmtCur     = (n)  => `$${(n || 0).toFixed(2)}`;

function empName(emp) {
  return emp.firstName
    ? `${emp.firstName} ${emp.lastName || ''}`.trim()
    : emp.name || emp.id;
}

function isOwnerRole(emp) {
  const role = (emp.role || '').toLowerCase();
  return role === 'owner' || role === 'manager' || emp.isOwner === true;
}

const MODES = [
  {
    key: 'keep',
    label: 'Keep Your Own',
    desc: 'Each server keeps 100% of the tips on their checks. Nothing is shared or pooled.',
    icon: '👤',
  },
  {
    key: 'share',
    label: 'Tip Share (Tip Out)',
    desc: 'Servers tip out a % of their tips or net sales to support staff like bussers and hosts.',
    icon: '↔️',
  },
  {
    key: 'pool',
    label: 'Tip Pool',
    desc: 'All tips combined into one pool, split among participants by role-weighted hours worked.',
    icon: '🫱🏽‍🫲🏼',
  },
];

const WEIGHT_PRESETS = [0.5, 0.75, 1.0, 1.25, 1.5];

// ── Main component ────────────────────────────────────────────────────────────

export default function TipDistributionView({ restaurantId }) {
  const [tab,        setTab]        = useState('settings');
  const [employees,  setEmployees]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [error,      setError]      = useState('');
  const [dirty,      setDirty]      = useState(false);

  // Mode
  const [mode, setMode] = useState('keep');

  // Share settings
  const [shareRules,           setShareRules]           = useState([]);
  const [shareCalculationBase, setShareCalculationBase] = useState('tips');

  // Pool settings
  const [poolParticipantIds, setPoolParticipantIds] = useState([]);
  const [poolWeights,        setPoolWeights]        = useState({});
  const [minHoursForPool,    setMinHoursForPool]    = useState(0);

  // Global settings
  const [settlementMethod,    setSettlementMethod]    = useState('cash');
  const [tipPeriod,           setTipPeriod]           = useState('daily');
  const [excludeOwners,       setExcludeOwners]       = useState(true);
  const [includeCompsInBase,  setIncludeCompsInBase]  = useState(false);
  const [separateAutoGratuity, setSeparateAutoGratuity] = useState(true);
  const [minSettlementCents,  setMinSettlementCents]  = useState(0);

  // Settle tab
  const [reportStart, setReportStart] = useState(todayStr());
  const [reportEnd,   setReportEnd]   = useState(todayStr());
  const [preview,     setPreview]     = useState(null);
  const [previewing,  setPreviewing]  = useState(false);
  const [settling,    setSettling]    = useState(false);
  const [history,     setHistory]     = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadEmployees = useCallback(async () => {
    const snap = await getDocs(collection(db, 'restaurants_data', restaurantId, 'employees'));
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(e => e.isActive !== false)
      .sort((a, b) => empName(a).localeCompare(empName(b)));
  }, [restaurantId]);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    setError('');
    Promise.all([loadEmployees(), _getConfig({ restaurantId })])
      .then(([emps, res]) => {
        setEmployees(emps);
        const cfg = res.data?.config;
        if (cfg) {
          setMode(cfg.mode || 'keep');
          setShareRules(cfg.shareRules || []);
          setShareCalculationBase(cfg.shareCalculationBase || 'tips');
          setPoolParticipantIds(cfg.poolParticipantIds || []);
          setPoolWeights(cfg.poolWeights || {});
          setMinHoursForPool(cfg.minHoursForPool || 0);
          setSettlementMethod(cfg.settlementMethod || 'cash');
          setTipPeriod(cfg.tipPeriod || 'daily');
          setExcludeOwners(cfg.excludeOwners !== false);
          setIncludeCompsInBase(cfg.includeCompsInBase === true);
          setSeparateAutoGratuity(cfg.separateAutoGratuity !== false);
          setMinSettlementCents(cfg.minSettlementCents || 0);
        }
      })
      .catch(e => setError('Failed to load: ' + e.message))
      .finally(() => setLoading(false));
  }, [restaurantId, loadEmployees]);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const res = await _getHistory({ restaurantId });
      setHistory(res.data?.settlements || []);
    } catch (e) {
      console.error('[TipDistribution] history error:', e);
    } finally {
      setHistLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    if (tab === 'settle') loadHistory();
  }, [tab, loadHistory]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const markDirty = () => { setDirty(true); setSaved(false); };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      if (mode === 'share' && shareCalculationBase === 'tips') {
        const totalPct = shareRules.reduce((s, r) => s + (Number(r.receivePercent) || 0), 0);
        if (totalPct > 100) {
          setError(`Tip-out percentages total ${totalPct}% — cannot exceed 100%.`);
          return;
        }
      }
      await _saveConfig({
        restaurantId,
        config: {
          mode,
          shareRules,
          shareCalculationBase,
          poolParticipantIds,
          poolWeights,
          minHoursForPool: Number(minHoursForPool) || 0,
          settlementMethod,
          tipPeriod,
          excludeOwners,
          includeCompsInBase,
          separateAutoGratuity,
          minSettlementCents: Number(minSettlementCents) || 0,
        },
      });
      setSaved(true); setDirty(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true); setPreview(null); setError('');
    try {
      const res = await _settle({
        restaurantId,
        periodStart: startOfDay(reportStart),
        periodEnd:   endOfDay(reportEnd),
        dryRun: true,
      });
      setPreview(res.data);
    } catch (e) {
      setError('Preview failed: ' + e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSettle = async () => {
    setSettling(true); setError('');
    try {
      await _settle({
        restaurantId,
        periodStart: startOfDay(reportStart),
        periodEnd:   endOfDay(reportEnd),
        dryRun: false,
      });
      setPreview(null);
      await loadHistory();
    } catch (e) {
      setError('Settlement failed: ' + e.message);
    } finally {
      setSettling(false);
    }
  };

  // ── Pool helpers ─────────────────────────────────────────────────────────

  const togglePoolParticipant = (empId) => {
    setPoolParticipantIds(ids =>
      ids.includes(empId) ? ids.filter(id => id !== empId) : [...ids, empId]
    );
    markDirty();
  };

  const setWeight = (empId, val) => {
    const num = Math.max(0, Math.min(5, Number(val) || 0));
    setPoolWeights(prev => ({ ...prev, [empId]: num }));
    markDirty();
  };

  // ── Share rule helpers ───────────────────────────────────────────────────

  const updateRule = (idx, patch) => {
    setShareRules(rules => rules.map((r, i) => i === idx ? { ...r, ...patch } : r));
    markDirty();
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  const poolCandidates = excludeOwners
    ? employees.filter(e => !isOwnerRole(e))
    : employees;

  const useNetSales = shareCalculationBase === 'net_sales';
  const totalTipOutPct = !useNetSales
    ? shareRules.reduce((s, r) => s + (Number(r.receivePercent) || 0), 0)
    : 0;
  const serverKeepPct = Math.max(0, 100 - totalTipOutPct);

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-neutral-400 text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 pt-2 pb-6 space-y-6">

      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-neutral-800 mb-1">Tip Distribution</h2>
        <p className="text-sm text-neutral-400">
          Configure how tips are shared among your staff and settle each period.
        </p>
        <div className="flex gap-2 mt-4 border-b border-neutral-700 pb-0">
          {[['settings', 'Settings'], ['settle', 'Settle & Reports']].map(([t, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                tab === t
                  ? 'border-brand-gold text-brand-gold'
                  : 'border-transparent text-neutral-400 hover:text-neutral-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-900/30 border border-red-500/40 rounded-lg px-4 py-3 text-red-300 text-sm flex items-start gap-3">
          <span className="shrink-0 mt-0.5">⚠</span>
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto shrink-0 text-red-400 hover:text-red-200">✕</button>
        </div>
      )}

      {/* ── SETTINGS TAB ───────────────────────────────────────────────────── */}
      {tab === 'settings' && (
        <div className="space-y-5">

          {/* Mode selection */}
          <div className="bg-neutral-900 rounded-xl border border-neutral-700 p-5">
            <h3 className="text-xs font-bold text-neutral-400 uppercase tracking-widest mb-4">
              Distribution Mode
            </h3>
            <div className="space-y-3">
              {MODES.map(m => (
                <button
                  key={m.key}
                  onClick={() => { setMode(m.key); markDirty(); }}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    mode === m.key
                      ? 'border-brand-blue bg-brand-blue/10'
                      : 'border-neutral-700 bg-neutral-800 hover:border-neutral-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl shrink-0">{m.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-sm ${mode === m.key ? 'text-white' : 'text-neutral-200'}`}>
                          {m.label}
                        </span>
                        {mode === m.key && (
                          <span className="text-xs bg-brand-blue text-white px-2 py-0.5 rounded-full">Active</span>
                        )}
                      </div>
                      <p className="text-xs text-neutral-400 mt-0.5 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* ── TIP SHARE settings ─────────────────────────────────────────── */}
          {mode === 'share' && (
            <>
              {/* Calculation base */}
              <div className="bg-neutral-900 rounded-xl border border-neutral-700 p-5 space-y-4">
                <div>
                  <h3 className="text-xs font-bold text-neutral-400 uppercase tracking-widest">
                    Calculate Tip-Out Based On
                  </h3>
                  <p className="text-xs text-neutral-500 mt-1">
                    Determines what percentage applies to for each server.
                  </p>
                </div>
                <div className="space-y-3">
                  {[
                    {
                      key: 'tips',
                      label: '% of Tips Collected',
                      desc: 'e.g. Bussers receive 10% of each server\'s voluntary tips.',
                      tag: null,
                    },
                    {
                      key: 'net_sales',
                      label: '% of Net Sales',
                      desc: 'e.g. Bussers receive 1.5% of each server\'s food & drink sales. Predictable regardless of tip %, easier for servers to calculate at shift end.',
                      tag: 'Industry Standard',
                    },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => { setShareCalculationBase(opt.key); markDirty(); }}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        shareCalculationBase === opt.key
                          ? 'border-brand-blue bg-brand-blue/10'
                          : 'border-neutral-700 bg-neutral-800 hover:border-neutral-600'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-sm font-bold ${shareCalculationBase === opt.key ? 'text-white' : 'text-neutral-300'}`}>
                          {opt.label}
                        </span>
                        {opt.tag && (
                          <span className="text-xs bg-brand-gold/20 text-brand-gold px-2 py-0.5 rounded-full">
                            {opt.tag}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-neutral-400 leading-relaxed">{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Comps toggle — only relevant for net_sales */}
                {useNetSales && (
                  <div className="flex items-start justify-between gap-4 pt-3 border-t border-neutral-700">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-neutral-300">Include Comped Items in Sales Base</p>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Off (default): tip-out calculated on what was actually charged — comped/discounted items excluded.
                        On: uses full item price before comps.
                      </p>
                    </div>
                    <Toggle value={includeCompsInBase} onChange={v => { setIncludeCompsInBase(v); markDirty(); }} />
                  </div>
                )}
              </div>

              {/* Support groups */}
              <div className="bg-neutral-900 rounded-xl border border-neutral-700 p-5 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xs font-bold text-neutral-400 uppercase tracking-widest">Support Groups</h3>
                    <p className="text-xs text-neutral-500 mt-1">
                      {useNetSales
                        ? 'Server keeps all their tips. Groups receive a % of each server\'s net sales.'
                        : (
                          <>Server keeps: <span className={`font-bold ${totalTipOutPct > 100 ? 'text-red-400' : 'text-green-400'}`}>{serverKeepPct}%</span>
                          {totalTipOutPct > 100 && <span className="text-red-400 ml-2">⚠ Exceeds 100%</span>}</>
                        )
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShareRules(r => [...r, {
                        id: uid(), label: '', receivePercent: 0,
                        splitByCategory: false, foodPercent: 0, drinkPercent: 0,
                        employeeIds: [],
                      }]);
                      markDirty();
                    }}
                    className="shrink-0 text-sm bg-brand-blue text-white px-3 py-1.5 rounded-lg font-semibold hover:opacity-80"
                  >
                    + Add Group
                  </button>
                </div>

                {shareRules.length === 0 && (
                  <p className="text-neutral-500 text-sm italic py-2">
                    No groups yet. Add one to define tip-out rules.
                  </p>
                )}

                {shareRules.map((rule, idx) => (
                  <div key={rule.id} className="bg-neutral-800 rounded-xl border border-neutral-700 p-4 space-y-3">
                    {/* Group name + remove */}
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        placeholder="Group name (e.g. Bussers)"
                        value={rule.label}
                        onChange={e => updateRule(idx, { label: e.target.value })}
                        className="flex-1 bg-neutral-700 border border-neutral-600 text-white text-sm rounded-lg px-3 py-2 placeholder-neutral-500"
                      />
                      <button
                        onClick={() => { setShareRules(r => r.filter(x => x.id !== rule.id)); markDirty(); }}
                        className="text-neutral-500 hover:text-red-400 text-lg leading-none shrink-0"
                      >
                        ✕
                      </button>
                    </div>

                    {/* Percentage input(s) */}
                    {useNetSales ? (
                      <>
                        {/* Food/drink category split toggle */}
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs text-neutral-400">Split food vs. bar rates</p>
                          <Toggle
                            value={rule.splitByCategory}
                            onChange={v => updateRule(idx, { splitByCategory: v })}
                          />
                        </div>

                        {rule.splitByCategory ? (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-neutral-500 mb-1">Food Sales %</label>
                              <div className="flex items-center gap-1 bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2">
                                <input
                                  type="number" min="0" max="100" step="0.25"
                                  value={rule.foodPercent || 0}
                                  onChange={e => updateRule(idx, { foodPercent: Number(e.target.value) })}
                                  className="w-12 bg-transparent text-white text-sm text-right outline-none"
                                />
                                <span className="text-neutral-400 text-sm">%</span>
                              </div>
                              <p className="text-xs text-neutral-600 mt-1">of server's food sales</p>
                            </div>
                            <div>
                              <label className="block text-xs text-neutral-500 mb-1">Bar/Drink Sales %</label>
                              <div className="flex items-center gap-1 bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2">
                                <input
                                  type="number" min="0" max="100" step="0.25"
                                  value={rule.drinkPercent || 0}
                                  onChange={e => updateRule(idx, { drinkPercent: Number(e.target.value) })}
                                  className="w-12 bg-transparent text-white text-sm text-right outline-none"
                                />
                                <span className="text-neutral-400 text-sm">%</span>
                              </div>
                              <p className="text-xs text-neutral-600 mt-1">of server's bar sales</p>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs text-neutral-500 mb-1">All Net Sales %</label>
                            <div className="flex items-center gap-1 bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 w-32">
                              <input
                                type="number" min="0" max="100" step="0.25"
                                value={rule.receivePercent}
                                onChange={e => updateRule(idx, { receivePercent: Number(e.target.value) })}
                                className="w-12 bg-transparent text-white text-sm text-right outline-none"
                              />
                              <span className="text-neutral-400 text-sm">%</span>
                            </div>
                            <p className="text-xs text-neutral-600 mt-1">of each server's net sales</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div>
                        <label className="block text-xs text-neutral-500 mb-1">Tips %</label>
                        <div className="flex items-center gap-1 bg-neutral-700 border border-neutral-600 rounded-lg px-3 py-2 w-32">
                          <input
                            type="number" min="0" max="100" step="0.5"
                            value={rule.receivePercent}
                            onChange={e => updateRule(idx, { receivePercent: Number(e.target.value) })}
                            className="w-12 bg-transparent text-white text-sm text-right outline-none"
                          />
                          <span className="text-neutral-400 text-sm">%</span>
                        </div>
                        <p className="text-xs text-neutral-600 mt-1">of each server's tips, split among members</p>
                      </div>
                    )}

                    {/* Employee picker */}
                    <div>
                      <p className="text-xs text-neutral-400 font-semibold mb-2">Who is in this group?</p>
                      <div className="flex flex-wrap gap-2">
                        {employees.map(emp => {
                          const name = empName(emp);
                          const selected = rule.employeeIds.includes(emp.id);
                          const ownerBlocked = excludeOwners && isOwnerRole(emp);
                          return (
                            <button
                              key={emp.id}
                              disabled={ownerBlocked}
                              onClick={() => {
                                if (ownerBlocked) return;
                                const ids = selected
                                  ? rule.employeeIds.filter(id => id !== emp.id)
                                  : [...rule.employeeIds, emp.id];
                                updateRule(idx, { employeeIds: ids });
                              }}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                ownerBlocked
                                  ? 'bg-neutral-800 border-neutral-700 text-neutral-600 cursor-not-allowed'
                                  : selected
                                  ? 'bg-brand-blue border-brand-blue text-white'
                                  : 'bg-neutral-700 border-neutral-600 text-neutral-300 hover:border-neutral-400'
                              }`}
                            >
                              {name}{ownerBlocked ? ' (excluded)' : ''}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── TIP POOL settings ──────────────────────────────────────────── */}
          {mode === 'pool' && (
            <div className="bg-neutral-900 rounded-xl border border-neutral-700 p-5 space-y-5">

              {/* Exclude owners toggle */}
              <div className="flex items-start justify-between gap-4 pb-5 border-b border-neutral-700">
                <div>
                  <h3 className="text-xs font-bold text-neutral-400 uppercase tracking-widest">Exclude Owners & Managers</h3>
                  <p className="text-xs text-neutral-500 mt-1">
                    Recommended. Required in California and many states.
                  </p>
                </div>
                <Toggle
                  value={excludeOwners}
                  onChange={v => {
                    setExcludeOwners(v);
                    if (v) {
                      setPoolParticipantIds(ids =>
                        ids.filter(id => {
                          const emp = employees.find(e => e.id === id);
                          return !emp || !isOwnerRole(emp);
                        })
                      );
                    }
                    markDirty();
                  }}
                />
              </div>

              {/* Minimum hours */}
              <div className="flex items-start justify-between gap-4 pb-5 border-b border-neutral-700">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs font-bold text-neutral-400 uppercase tracking-widest">Minimum Hours to Qualify</h3>
                  <p className="text-xs text-neutral-500 mt-1">
                    Staff who worked fewer hours in the period are excluded from the pool. Shown to employees in the app.
                  </p>
                </div>
                <div className="flex items-center gap-1.5 bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 shrink-0">
                  <input
                    type="number" min="0" max="24" step="0.5"
                    value={minHoursForPool}
                    onChange={e => { setMinHoursForPool(Number(e.target.value)); markDirty(); }}
                    className="w-10 bg-transparent text-white text-sm text-right outline-none"
                  />
                  <span className="text-neutral-400 text-sm">hrs</span>
                </div>
              </div>

              {/* Participants + weights */}
              <div>
                <h3 className="text-xs font-bold text-neutral-400 uppercase tracking-widest mb-1">Participants & Role Weights</h3>
                <p className="text-xs text-neutral-500 mb-4 leading-relaxed">
                  Share = <strong className="text-neutral-300">hours × weight</strong> / total.
                  Typical: Server 1.0 · Bartender 1.0 · Busser 0.75 · Host 0.5
                </p>
                <div className="space-y-2">
                  {poolCandidates.map(emp => {
                    const name = empName(emp);
                    const selected = poolParticipantIds.includes(emp.id);
                    const weight = typeof poolWeights[emp.id] === 'number' ? poolWeights[emp.id] : 1.0;
                    return (
                      <div
                        key={emp.id}
                        className={`rounded-xl border p-3 transition-all ${
                          selected ? 'border-brand-blue bg-brand-blue/10' : 'border-neutral-700 bg-neutral-800'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => togglePoolParticipant(emp.id)}
                            className={`shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              selected ? 'bg-brand-blue border-brand-blue' : 'border-neutral-500 bg-transparent'
                            }`}
                          >
                            {selected && <span className="text-white text-xs font-bold">✓</span>}
                          </button>
                          <span className={`flex-1 text-sm font-semibold ${selected ? 'text-white' : 'text-neutral-400'}`}>
                            {name}
                            {emp.role && <span className="ml-1.5 text-xs font-normal text-neutral-500">{emp.role}</span>}
                          </span>
                        </div>
                        {selected && (
                          <div className="flex items-center gap-2 mt-2 ml-8">
                            <span className="text-xs text-neutral-500 shrink-0">Weight:</span>
                            <div className="flex gap-1 flex-wrap">
                              {WEIGHT_PRESETS.map(p => (
                                <button
                                  key={p}
                                  onClick={() => setWeight(emp.id, p)}
                                  className={`text-xs px-2 py-1 rounded border transition-colors ${
                                    weight === p
                                      ? 'bg-brand-blue border-brand-blue text-white'
                                      : 'border-neutral-600 bg-neutral-700 text-neutral-300 hover:border-neutral-400'
                                  }`}
                                >
                                  {p}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {poolCandidates.length === 0 && (
                    <p className="text-neutral-500 text-sm italic">No eligible employees found.</p>
                  )}
                </div>
                {poolParticipantIds.length > 0 && (
                  <p className="text-xs text-neutral-500 mt-3">
                    {poolParticipantIds.length} participant{poolParticipantIds.length !== 1 ? 's' : ''}
                    {minHoursForPool > 0 && ` · Must work ${minHoursForPool}+ hours to qualify`}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── Settlement & Global settings ────────────────────────────────── */}
          {mode !== 'keep' && (
            <div className="bg-neutral-900 rounded-xl border border-neutral-700 p-5 space-y-5">
              <h3 className="text-xs font-bold text-neutral-400 uppercase tracking-widest">Settlement & Compliance</h3>

              {/* Method */}
              <div>
                <p className="text-xs text-neutral-400 font-semibold mb-2">Settlement Method</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'cash',    label: 'Cash',    desc: 'Reports show amounts. Staff settle in cash at shift end.' },
                    { key: 'payroll', label: 'Payroll', desc: 'Auto-posts to each employee\'s payroll ledger when you settle.' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => { setSettlementMethod(opt.key); markDirty(); }}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${
                        settlementMethod === opt.key
                          ? 'border-brand-blue bg-brand-blue/10'
                          : 'border-neutral-700 bg-neutral-800 hover:border-neutral-600'
                      }`}
                    >
                      <div className={`text-sm font-bold ${settlementMethod === opt.key ? 'text-white' : 'text-neutral-300'}`}>
                        {opt.label}
                      </div>
                      <div className="text-xs text-neutral-400 mt-1 leading-relaxed">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Period */}
              <div>
                <p className="text-xs text-neutral-400 font-semibold mb-2">Settle Per</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'daily',  label: 'Shift / Day', desc: 'Settle at the end of each business day.' },
                    { key: 'weekly', label: 'Pay Week',    desc: 'Settle once per pay period alongside payroll.' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => { setTipPeriod(opt.key); markDirty(); }}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${
                        tipPeriod === opt.key
                          ? 'border-brand-blue bg-brand-blue/10'
                          : 'border-neutral-700 bg-neutral-800 hover:border-neutral-600'
                      }`}
                    >
                      <div className={`text-sm font-bold ${tipPeriod === opt.key ? 'text-white' : 'text-neutral-300'}`}>
                        {opt.label}
                      </div>
                      <div className="text-xs text-neutral-400 mt-1">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Auto-gratuity */}
              <div className="flex items-start justify-between gap-4 pt-2 border-t border-neutral-700">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-neutral-300">Treat Auto-Gratuity Separately</p>
                  <p className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                    On (recommended): service charge / auto-gratuity recorded in the POS is excluded from the
                    tip pool and handled through payroll wages instead.
                    Off: auto-grat is added to the tip pool total.
                  </p>
                </div>
                <Toggle value={separateAutoGratuity} onChange={v => { setSeparateAutoGratuity(v); markDirty(); }} />
              </div>

              {/* Exclude owners (share mode) */}
              {mode === 'share' && (
                <div className="flex items-start justify-between gap-4 border-t border-neutral-700 pt-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-neutral-300">Exclude Owners & Managers</p>
                    <p className="text-xs text-neutral-500 mt-0.5">Prevents adding owners/managers to any tip group.</p>
                  </div>
                  <Toggle value={excludeOwners} onChange={v => { setExcludeOwners(v); markDirty(); }} />
                </div>
              )}

              {/* Minimum settlement amount */}
              <div className="flex items-center justify-between gap-4 border-t border-neutral-700 pt-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-neutral-300">Minimum Settlement Amount</p>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Skip payroll entries below this amount. Avoids $0.03 ledger noise.
                  </p>
                </div>
                <div className="flex items-center gap-1 bg-neutral-800 border border-neutral-600 rounded-lg px-3 py-2 shrink-0">
                  <span className="text-neutral-400 text-sm">$</span>
                  <input
                    type="number" min="0" max="100" step="0.25"
                    value={(minSettlementCents / 100).toFixed(2)}
                    onChange={e => { setMinSettlementCents(Math.round(Number(e.target.value) * 100)); markDirty(); }}
                    className="w-14 bg-transparent text-white text-sm outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Save bar */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                saved
                  ? 'bg-green-700 text-white'
                  : dirty
                  ? 'bg-brand-blue text-white hover:opacity-80'
                  : 'bg-neutral-700 text-neutral-500 cursor-not-allowed'
              }`}
            >
              {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Settings'}
            </button>
            {dirty && !saving && (
              <span className="text-xs text-amber-400">Unsaved changes</span>
            )}
          </div>
        </div>
      )}

      {/* ── SETTLE & REPORTS TAB ─────────────────────────────────────────────── */}
      {tab === 'settle' && (
        <div className="space-y-5">

          {mode === 'keep' && (
            <div className="bg-neutral-900 rounded-xl border border-neutral-700 p-6 text-center">
              <p className="text-neutral-400 text-sm">
                Mode is set to <span className="text-white font-bold">Keep Your Own</span> — no settlement needed.
              </p>
            </div>
          )}

          {mode !== 'keep' && (
            <>
              {/* Date range */}
              <div className="bg-neutral-900 rounded-xl border border-neutral-700 p-5">
                <h3 className="text-xs font-bold text-neutral-400 uppercase tracking-widest mb-4">Preview Period</h3>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1">From</label>
                    <input
                      type="date" value={reportStart}
                      onChange={e => { setReportStart(e.target.value); setPreview(null); }}
                      className="bg-neutral-800 border border-neutral-600 text-white text-sm rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1">To</label>
                    <input
                      type="date" value={reportEnd}
                      onChange={e => { setReportEnd(e.target.value); setPreview(null); }}
                      className="bg-neutral-800 border border-neutral-600 text-white text-sm rounded-lg px-3 py-2"
                    />
                  </div>
                  <button
                    onClick={handlePreview}
                    disabled={previewing}
                    className="bg-brand-blue text-white px-5 py-2 rounded-xl text-sm font-bold hover:opacity-80 disabled:opacity-50"
                  >
                    {previewing ? 'Loading…' : 'Run Preview'}
                  </button>
                </div>
              </div>

              {/* Preview results */}
              {preview && (
                <div className="bg-neutral-900 rounded-xl border border-neutral-700 overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-700">
                    <div>
                      <h3 className="text-sm font-bold text-white">Distribution Preview</h3>
                      <p className="text-xs text-neutral-400 mt-0.5 space-x-2">
                        <span>{fmtDate(startOfDay(reportStart))} – {fmtDate(endOfDay(reportEnd))}</span>
                        {settlementMethod === 'payroll' && <span className="text-brand-gold">· Payroll on settle</span>}
                        {preview.shareCalculationBase === 'net_sales' && <span>· Net sales basis</span>}
                        {preview.includeCompsInBase && <span className="text-amber-400">· Comps included</span>}
                        {!preview.separateAutoGratuity && <span className="text-amber-400">· Auto-grat in pool</span>}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-neutral-400">Total Tips</div>
                      <div className="text-lg font-black text-green-400">{fmtCur(preview.totalTipsCollected)}</div>
                    </div>
                  </div>

                  {preview.distributions.length === 0 ? (
                    <div className="px-5 py-10 text-center text-neutral-500 text-sm">
                      No tips found for this period.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-neutral-800 text-xs text-neutral-400">
                            <th className="text-left px-5 py-3 font-semibold">Employee</th>
                            {mode === 'share' && (
                              <>
                                {preview.shareCalculationBase === 'net_sales' && (
                                  <>
                                    <th className="text-right px-3 py-3 font-semibold">Food</th>
                                    <th className="text-right px-3 py-3 font-semibold">Bar</th>
                                  </>
                                )}
                                <th className="text-right px-3 py-3 font-semibold">Tips</th>
                                <th className="text-right px-3 py-3 font-semibold text-red-400">Out</th>
                                <th className="text-right px-3 py-3 font-semibold text-green-400">In</th>
                              </>
                            )}
                            {mode === 'pool' && (
                              <>
                                <th className="text-right px-3 py-3 font-semibold">Hrs</th>
                                <th className="text-right px-3 py-3 font-semibold">Wt</th>
                                <th className="text-right px-3 py-3 font-semibold">Wtd</th>
                                <th className="text-right px-3 py-3 font-semibold">%</th>
                              </>
                            )}
                            <th className="text-right px-5 py-3 font-semibold">Net Tip</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.distributions.map((d, i) => (
                            <tr
                              key={i}
                              className={`border-b border-neutral-800 hover:bg-neutral-800/40 ${
                                d.qualified === false || d.belowMinimum ? 'opacity-50' : ''
                              }`}
                            >
                              <td className="px-5 py-3">
                                <div className="font-semibold text-white">{d.employeeName}</div>
                                <div className="text-xs text-neutral-500 mt-0.5">
                                  {mode === 'share'
                                    ? (d.type === 'server' ? 'Server' : d.groupLabel)
                                    : d.qualified === false
                                    ? <span className="text-amber-400">{d.disqualifiedReason}</span>
                                    : `${d.hoursWorked?.toFixed(1)}h × ${d.weight?.toFixed(2)}x`}
                                  {d.belowMinimum && (
                                    <span className="text-amber-400 ml-2">below minimum</span>
                                  )}
                                </div>
                              </td>

                              {mode === 'share' && (
                                <>
                                  {preview.shareCalculationBase === 'net_sales' && (
                                    <>
                                      <td className="text-right px-3 py-3 text-neutral-400 text-xs">
                                        {d.foodSales != null ? fmtCur(d.foodSales) : '—'}
                                      </td>
                                      <td className="text-right px-3 py-3 text-neutral-400 text-xs">
                                        {d.drinkSales != null ? fmtCur(d.drinkSales) : '—'}
                                      </td>
                                    </>
                                  )}
                                  <td className="text-right px-3 py-3 text-neutral-300">
                                    {d.tipsEarned > 0 ? fmtCur(d.tipsEarned) : '—'}
                                  </td>
                                  <td className="text-right px-3 py-3 text-red-400">
                                    {d.tipOut > 0 ? `-${fmtCur(d.tipOut)}` : '—'}
                                  </td>
                                  <td className="text-right px-3 py-3 text-green-400">
                                    {d.tipIn > 0 ? `+${fmtCur(d.tipIn)}` : '—'}
                                  </td>
                                </>
                              )}

                              {mode === 'pool' && (
                                <>
                                  <td className="text-right px-3 py-3 text-neutral-300 text-xs">
                                    {d.hoursWorked?.toFixed(1)}h
                                  </td>
                                  <td className="text-right px-3 py-3 text-neutral-400 text-xs">
                                    {d.weight?.toFixed(2)}×
                                  </td>
                                  <td className="text-right px-3 py-3 text-neutral-300 text-xs">
                                    {d.weightedHours?.toFixed(1)}
                                  </td>
                                  <td className="text-right px-3 py-3 text-neutral-300 text-xs">
                                    {d.poolShare?.toFixed(1)}%
                                  </td>
                                </>
                              )}

                              <td className={`text-right px-5 py-3 font-bold ${
                                d.qualified === false ? 'text-neutral-600'
                                : d.netTip >= 0 ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {d.qualified === false ? '—' : fmtCur(d.netTip)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {preview.distributions.some(d => d.qualified !== false && !d.belowMinimum && d.netTip > 0) && (
                    <div className="flex items-center justify-between px-5 py-4 border-t border-neutral-700 bg-neutral-800/30">
                      <p className="text-xs text-neutral-400">
                        {settlementMethod === 'payroll'
                          ? 'Settling posts tip amounts to each employee\'s payroll ledger.'
                          : 'Settling records this period. Staff settle cash amounts manually.'}
                      </p>
                      <button
                        onClick={handleSettle}
                        disabled={settling}
                        className="shrink-0 bg-green-700 hover:bg-green-600 text-white px-5 py-2 rounded-xl text-sm font-bold disabled:opacity-50 ml-4"
                      >
                        {settling ? 'Settling…' : 'Settle Period'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Settlement history */}
              <div className="bg-neutral-900 rounded-xl border border-neutral-700 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-bold text-neutral-400 uppercase tracking-widest">Settlement History</h3>
                  <button onClick={loadHistory} className="text-xs text-neutral-500 hover:text-neutral-300">Refresh</button>
                </div>

                {histLoading ? (
                  <div className="text-neutral-500 text-sm text-center py-4">Loading…</div>
                ) : history.length === 0 ? (
                  <p className="text-neutral-500 text-sm italic">No settlements yet.</p>
                ) : (
                  <div className="space-y-2">
                    {history.map(s => (
                      <div key={s.id} className="flex items-center justify-between bg-neutral-800 rounded-xl px-4 py-3 gap-4">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-white">
                            {fmtDate(s.periodStart)} – {fmtDate(s.periodEnd)}
                          </div>
                          <div className="text-xs text-neutral-400 mt-0.5">
                            {s.settledByName ? `Settled by ${s.settledByName}` : 'Settled'}
                            {s.settledAt ? ` · ${fmtDate(s.settledAt)}` : ''}
                            {s.shareCalculationBase === 'net_sales' && <span className="ml-1">· net sales</span>}
                            {s.payrollPosted && <span className="ml-1 text-green-400 font-semibold">· Payroll posted</span>}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-bold text-green-400">{fmtCur(s.totalTipsCollected)}</div>
                          <div className="text-xs text-neutral-500">{s.distributions?.length || 0} people</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Toggle component ──────────────────────────────────────────────────────────

function Toggle({ value, onChange }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        value ? 'bg-brand-blue' : 'bg-neutral-600'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        value ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
  );
}
